import { useState } from 'react';
import { useVinExtractionService } from '../../services/vinExtractionService';
import { failedNeighbour, failedPosition, parseBatchCars } from '../../utils/vinExtraction';
import { useLatestRequest } from './useVinRequest';

const idle = () => ({ loading: false, error: null });

/**
 * Loads the failed-cars page a step lands on, then moves to `target.index` on it — or to the nearest car still
 * there, since the failed set can shrink between pages. A page with no cars (or an unreadable body) is 'invalid'.
 */
const useFailedPageFetch = ({ batchId, search, pageSize, latest, setList, setState }) => {
    const { getBatchCars } = useVinExtractionService();

    return async target => {
        const isCurrent = latest.begin();
        setState({ loading: true, error: null });
        try {
            const params = { status: 'failed', search, page: target.page, pageSize };
            const data = parseBatchCars(await getBatchCars(batchId, params));
            if (!isCurrent()) return;
            const index = data ? Math.min(target.index, data.items.length - 1) : -1;
            if (index < 0) return setState({ loading: false, error: 'invalid' });
            setList({ items: data.items, page: target.page, pageSize, total: data.total, index });
            setState(idle());
        } catch (error) {
            if (!isCurrent()) return;
            setState({ loading: false, error });
        }
    };
};

/** What the drawer shows of the list: the current car, "k of N failed", and whether each step has a car. */
const navView = (list, neighbour) => ({
    active: list !== null,
    car: list ? list.items[list.index] : null,
    position: list ? failedPosition(list) : null,
    total: list ? list.total : null,
    hasPrevious: neighbour(-1) !== null,
    hasNext: neighbour(1) !== null,
});

/**
 * Previous / next through a batch's failed cars for the drawer (E22). It starts from the Failed tab's loaded page;
 * stepping past that page's edge fetches the neighbouring page with `status: 'failed'` and the same search.
 * `list` = {items, page, pageSize, total, index}; null while the drawer was not opened from the Failed tab.
 */
export const useFailedCarNav = ({ batchId, search }) => {
    const [list, setList] = useState(null);
    const [state, setState] = useState(idle);
    const latest = useLatestRequest();
    const fetchPage = useFailedPageFetch({ batchId, search, pageSize: list?.pageSize, latest, setList, setState });

    const start = initial => {
        latest.invalidate();
        setState(idle());
        setList(initial);
    };

    const neighbour = step => (list ? failedNeighbour({ ...list, itemsLength: list.items.length }, step) : null);

    const move = step => {
        const target = neighbour(step);
        if (!target || state.loading) return;
        if (target.fetch) return fetchPage(target);
        setState(idle());
        setList(prev => ({ ...prev, index: target.index }));
    };

    return {
        ...navView(list, neighbour),
        loading: state.loading,
        error: state.error,
        start,
        stop: () => start(null),
        previous: () => move(-1),
        next: () => move(1),
    };
};
